import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase/config';
import { AdAnalyticsDebug } from './ads/AdAnalyticsDebug';

const DebugPanel: React.FC = () => {
  const [results, setResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const testFunction = async (functionName: string, data: any = {}) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const callable = httpsCallable(functions, functionName);
      const result = await callable(data);
      setResults({
        function: functionName,
        data: result.data,
        timestamp: new Date().toISOString()
      });
    } catch (err: any) {
      console.error(`Error calling ${functionName}:`, err);
      setError(`${functionName}: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-sky-neutral border border-urban-200 rounded-lg p-4 mt-6">
      <h3 className="text-lg font-semibold mb-4 text-urban">🔧 Debug Panel</h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <button
          onClick={() => testFunction('healthCheck')}
          disabled={isLoading}
          className="px-3 py-2 bg-cardboard text-white rounded text-sm hover:bg-cardboard-600 disabled:opacity-50"
        >
          Health Check
        </button>
        
        <button
          onClick={() => testFunction('getReports')}
          disabled={isLoading}
          className="px-3 py-2 bg-verge text-white rounded text-sm hover:bg-verge-600 disabled:opacity-50"
        >
          Get All Reports
        </button>
        
        <button
          onClick={() => testFunction('getReports', { status: 'active' })}
          disabled={isLoading}
          className="px-3 py-2 bg-treasure text-urban rounded text-sm hover:bg-treasure-600 disabled:opacity-50"
        >
          Get Active Reports
        </button>
        
        <button
          onClick={() => testFunction('getReports', { limit: 5 })}
          disabled={isLoading}
          className="px-3 py-2 bg-urban text-white rounded text-sm hover:bg-urban-600 disabled:opacity-50"
        >
          Get 5 Reports
        </button>
        
        <button
          onClick={() => testFunction('getReports', { status: 'in_review' })}
          disabled={isLoading}
          className="px-3 py-2 bg-teddy-500 text-urban rounded text-sm hover:bg-teddy-600 disabled:opacity-50"
        >
          Get In Review
        </button>
        
        <button
          onClick={() => testFunction('setupTestUsers')}
          disabled={isLoading}
          className="px-3 py-2 bg-sky-500 text-white rounded text-sm hover:bg-sky-600 disabled:opacity-50 col-span-2"
        >
          Setup Test Users (Emulator Only)
        </button>
        
        <button
          onClick={() => testFunction('getReports', {})}
          disabled={isLoading}
          className="px-3 py-2 bg-rust text-white rounded text-sm hover:bg-rust-600 disabled:opacity-50"
        >
          Get All (No Filter)
        </button>
      </div>

      {isLoading && (
        <div className="text-center py-4">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-cardboard"></div>
          <p className="mt-2 text-sm text-urban-300">Testing function...</p>
        </div>
      )}

      {error && (
        <div className="bg-rust-50 border border-rust-200 rounded p-3 mb-4">
          <p className="text-rust-800 text-sm">{error}</p>
        </div>
      )}

      {results && (
        <div className="bg-teddy border border-urban-200 rounded p-3">
          <h4 className="font-semibold text-sm mb-2 text-urban">
            Results from {results.function} ({results.timestamp})
          </h4>
          <pre className="text-xs bg-sky-neutral p-2 rounded overflow-auto max-h-64 text-urban">
            {JSON.stringify(results.data, null, 2)}
          </pre>
        </div>
      )}

      {/* Ad Analytics Debug */}
      <AdAnalyticsDebug />
    </div>
  );
};

export default DebugPanel;